import _ from "lodash";
import { action, computed, decorate, observable } from "mobx";

class Clause {
  defaults = {
    emptyList: [],
    emptyString: "",
    unsetNumber: -1
  };
  category = this.defaults["emptyString"];
  fieldId = this.defaults["unsetNumber"];
  fieldHelperText = this.defaults["emptyString"];
  fieldName = this.defaults["emptyString"];
  hasNext = false;
  operator = this.defaults["emptyString"];
  type = this.defaults["emptyString"];
  value = this.defaults["emptyString"];
  values = this.defaults["emptyList"];
  logicalConjunctionOperator = this.defaults["emptyString"];
  logicalConjunctionOperatorErrors = [];
  operatorErrors = [];
  valueErrors = [];
  valuesErrors = [];
  showBorder = false;

  resetFieldData() {
    this.fieldId = this.defaults["unsetNumber"];
    this.fieldName = this.defaults["emptyString"];
  }

  resetOperator() {
    this.operator = this.defaults["emptyString"];
    this.operatorErrors = [];
  }

  resetType() {
    this.type = this.defaults["emptyString"];
  }

  resetValue() {
    this.value = this.defaults["emptyString"];
    this.fieldHelperText = this.defaults["emptyString"];
    this.valueErrors = [];
  }

  resetValues() {
    this.values = this.defaults["emptyList"];
    this.fieldHelperText = this.defaults["emptyString"];
    this.valuesErrors = [];
  }

  isLogicalConjunctionOperatorValid() {
    if (
      this.hasNext &&
      this.logicalConjunctionOperator === this.defaults["emptyString"]
    ) {
      this.logicalConjunctionOperatorErrors.push(
        "Can't be None when followed by clause"
      );
      return false;
    } else {
      return true;
    }
  }

  isOperatorValid() {
    if (this.operator === this.defaults["emptyString"]) {
      this.operatorErrors.push("Can't be blank");
      return false;
    } else {
      return true;
    }
  }

  isValueValid(fieldsWithDistinctValues) {
    let result = true;
    if (fieldsWithDistinctValues.includes(this.fieldName)) {
      if (this.values.length === 0) {
        this.valuesErrors.push("Can't be blank");
        result = false;
      }
    } else {
      if (this.value === this.defaults["emptyString"]) {
        this.valueErrors.push("Can't be blank");
        result = false;
      }
      if (
        this.type === "NUMBER" &&
        typeof this.value === "string" &&
        _.some(
          this.value
            .split(",")
            .map(Number)
            .map(isNaN)
        )
      ) {
        this.valueErrors.push("Must be a number");
        result = false;
      }
    }
    return result;
  }

  resetErrors() {
    this.logicalConjunctionOperatorErrors = [];
    this.operatorErrors = [];
    this.valueErrors = [];
    this.valuesErrors = [];
  }

  isValid(fieldsWithDistinctValues) {
    this.resetErrors();
    const results = _.map(
      [
        this.isLogicalConjunctionOperatorValid.bind(this),
        this.isOperatorValid.bind(this),
        this.isValueValid.bind(this, fieldsWithDistinctValues)
      ],
      f => f()
    );
    return _.every(results, r => r === true);
  }

  get hasLogicalConjunctionOperatorErrors() {
    return this.logicalConjunctionOperatorErrors.length > 0;
  }

  get hasOperatorErrors() {
    return this.operatorErrors.length > 0;
  }

  get hasValueErrors() {
    return this.valueErrors.length > 0;
  }

  get hasValuesErrors() {
    return this.valuesErrors.length > 0;
  }

  get operatorErrorMessage() {
    return this.operatorErrors.join(", ");
  }

  get logicalConjunctionOperatorErrorMessage() {
    return this.logicalConjunctionOperatorErrors.join(", ");
  }

  get valueErrorMessage() {
    return this.valueErrors.join(", ");
  }

  get valuesErrorMessage() {
    return this.valuesErrors.join(", ");
  }

  get isFieldNameDisabled() {
    return this.category === "";
  }

  get isOperatorDisabled() {
    return this.fieldId === -1;
  }

  get isComplete() {
    return (
      this.fieldId !== -1 &&
      this.operator !== "" &&
      (this.value !== "" || this.values.length > 0)
    );
  }

  get asString() {
    const value =
      this.values.length > 0
        ? this.values.map(v => v.label).join(",")
        : this.value;
    return `${this.fieldName} ${this.operator} ${value}`;
  }
}

decorate(Clause, {
  category: observable,
  fieldId: observable,
  fieldHelperText: observable,
  fieldName: observable,
  hasNext: observable,
  operator: observable,
  operatorErrors: observable,
  type: observable,
  value: observable,
  valueErrors: observable,
  values: observable,
  valuesErrors: observable,
  logicalConjunctionOperator: observable,
  logicalConjunctionOperatorErrors: observable,
  showBorder: observable,
  isValid: action,
  resetFieldData: action,
  resetOperator: action,
  resetType: action,
  resetValue: action,
  resetValues: action,
  hasLogicalConjunctionOperatorErrors: computed,
  hasOperatorErrors: computed,
  hasValueErrors: computed,
  hasValuesErrors: computed,
  isFieldNameDisabled: computed,
  isOperatorDisabled: computed,
  isComplete: computed,
  operatorErrorMessage: computed,
  asString: computed,
  logicalConjunctionOperatorErrorMessage: computed,
  valueErrorMessage: computed,
  valuesErrorMessage: computed
});

export default Clause;



// WEBPACK FOOTER //
// ./src/models/Clause.js